// frontend/src/pages/Analytics.jsx
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';
import { useLanguage } from '../utils/i18n';
import { api } from '../utils/api';
import { useQuery } from '@tanstack/react-query';
import Logo from '../components/Logo';
import LanguageToggle from '../components/LanguageToggle';
import {
    LayoutDashboard, Users, Palette, Settings,
    ChevronLeft, TrendingUp, Trophy, Clock, Gift,
    Zap, BarChart3
} from 'lucide-react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
    ResponsiveContainer, AreaChart, Area, PieChart, Pie, Cell, Legend
} from 'recharts';
import './Analytics.css';

// ─── Time Ago Helper ─────────────────────────────────────────
function formatTimeAgo(dateStr, t) {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return t('time_just_now');
    if (mins < 60) return `${mins}m ${t('time_ago')}`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ${t('time_ago')}`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ${t('time_ago')}`;
    const months = Math.floor(days / 30);
    return `${months}mo ${t('time_ago')}`;
}

// ─── Custom Tooltips ─────────────────────────────────────────
const CustomTooltip = ({ active, payload, label, visitLabel }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="analytics-tooltip">
            <p className="analytics-tooltip-label">{label}</p>
            <p className="analytics-tooltip-value">{payload[0].value} {visitLabel}</p>
        </div>
    );
};

const RETENTION_COLORS = ['#6366f1', '#2d3748'];

// ─── Page ────────────────────────────────────────────────────
export default function AnalyticsPage() {
    const { business } = useAuth();
    const { t } = useLanguage();
    const [chartPeriod, setChartPeriod] = useState('week'); // week | month | year

    // Fetch real analytics data
    const { data: analytics, isLoading } = useQuery({
        queryKey: ['analytics', business?.id],
        queryFn: () => api.getAnalytics(business.id),
        enabled: !!business?.id,
        refetchInterval: 60000, // refresh every minute
        staleTime: 30000,
    });

    if (!business) return (
        <div className="page container" style={{ textAlign: 'center', paddingTop: 80 }}>
            <p>{t('loading')}</p>
        </div>
    );

    // Destructure with safe defaults
    const metrics = analytics?.metrics || {};
    const dailyVisits = analytics?.dailyVisits || [];
    const monthlyVisits = analytics?.monthlyVisits || [];
    const yearlyVisits = analytics?.yearlyVisits || [];
    const hourlyVisits = analytics?.hourlyVisits || [];
    const retentionData = analytics?.retentionData || [{ name: t('analytics_returning'), value: 0 }, { name: t('analytics_new'), value: 0 }];
    const topClients = analytics?.topClients || [];
    const insights = analytics?.insights || {};

    const chartData = chartPeriod === 'week' ? dailyVisits
        : chartPeriod === 'month' ? monthlyVisits
            : yearlyVisits;

    // Calculate retention percentages for the donut
    const retentionTotal = retentionData.reduce((s, d) => s + d.value, 0);
    // Map backend names to translated labels
    const translatedRetention = retentionData.map(d => ({
        ...d,
        name: d.name === 'Recurrentes' ? t('analytics_returning') : d.name === 'Nuevos' ? t('analytics_new') : d.name
    }));
    const retentionPercent = retentionTotal === 0
        ? [{ name: t('analytics_returning'), value: 0 }, { name: t('analytics_new'), value: 100 }]
        : translatedRetention.map(d => ({ ...d, percent: Math.round((d.value / retentionTotal) * 100) }));

    return (
        <div className="premium-dashboard">
            {/* Fixed Header */}
            <header className="fixed-header">
                <Link to="/dashboard" className="nav-brand-group" style={{ textDecoration: 'none' }}>
                    <ChevronLeft size={24} color="var(--text-muted)" />
                    <span className="brand-text" style={{ fontSize: 18, marginLeft: 4 }}>{t('analytics_title')}</span>
                </Link>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div className="header-biz-name">{business.name}</div>
                    <LanguageToggle />
                </div>
            </header>

            {/* Scrollable Content */}
            <div className="dashboard-content-scroll">

                {isLoading ? (
                    <div style={{ textAlign: 'center', paddingTop: 60, color: 'var(--text-muted)' }}>
                        {t('loading')}
                    </div>
                ) : (
                    <>
                        {/* ═══ 1. TOP METRICS ROW ═══ */}
                        <section className="metrics-grid-premium analytics-metrics-grid">
                            <MetricCard icon={Zap} value={metrics.weeklyVisits || 0} label={t('analytics_visits_this_week')} trend={metrics.visitsTrend} />
                            <MetricCard icon={Users} value={`${metrics.retentionRate || 0}%`} label={t('analytics_retention_rate')} trend={metrics.retentionTrend} />
                            <MetricCard icon={BarChart3} value={metrics.stampsThisWeek || 0} label={t('analytics_stamps_given')} trend={metrics.stampsTrend} />
                            <MetricCard icon={TrendingUp} value={`${metrics.conversionRate || 0}%`} label={t('analytics_conversion_rate')} />
                        </section>

                        {/* ═══ 2. MAIN BAR CHART ═══ */}
                        <section className="analytics-section">
                            <div className="analytics-card">
                                <div className="analytics-card-header">
                                    <h3 className="analytics-card-title">{t('analytics_visits_per_day')}</h3>
                                    <div className="analytics-tabs">
                                        <button
                                            className={`analytics-tab ${chartPeriod === 'week' ? 'active' : ''}`}
                                            onClick={() => setChartPeriod('week')}
                                        >{t('analytics_tab_week')}</button>
                                        <button
                                            className={`analytics-tab ${chartPeriod === 'month' ? 'active' : ''}`}
                                            onClick={() => setChartPeriod('month')}
                                        >{t('analytics_tab_month')}</button>
                                        <button
                                            className={`analytics-tab ${chartPeriod === 'year' ? 'active' : ''}`}
                                            onClick={() => setChartPeriod('year')}
                                        >{t('analytics_tab_year')}</button>
                                    </div>
                                </div>
                                <div className="analytics-chart-container">
                                    <ResponsiveContainer width="100%" height={280}>
                                        <BarChart data={chartData} barCategoryGap="25%">
                                            <defs>
                                                <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="0%" stopColor="#8b5cf6" />
                                                    <stop offset="100%" stopColor="#6366f1" />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                                            <XAxis
                                                dataKey="day"
                                                tick={{ fill: 'var(--text-dim)', fontSize: 12 }}
                                                tickLine={false}
                                                axisLine={false}
                                            />
                                            <YAxis
                                                tick={{ fill: 'var(--text-dim)', fontSize: 12 }}
                                                tickLine={false}
                                                axisLine={false}
                                                width={30}
                                            />
                                            <RechartsTooltip content={<CustomTooltip visitLabel={t('analytics_visits')} />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
                                            <Bar dataKey="visits" fill="url(#barGrad)" radius={[6, 6, 0, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </section>

                        {/* ═══ 3. SECONDARY CHARTS ═══ */}
                        <section className="analytics-section analytics-two-col">
                            {/* Area Chart */}
                            <div className="analytics-card">
                                <div className="analytics-card-header">
                                    <h3 className="analytics-card-title">{t('analytics_visits_per_hour')}</h3>
                                </div>
                                <div className="analytics-chart-container">
                                    <ResponsiveContainer width="100%" height={220}>
                                        <AreaChart data={hourlyVisits}>
                                            <defs>
                                                <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="0%" stopColor="#5B6EF5" stopOpacity={0.4} />
                                                    <stop offset="100%" stopColor="#5B6EF5" stopOpacity={0.05} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                                            <XAxis
                                                dataKey="hour"
                                                tick={{ fill: 'var(--text-dim)', fontSize: 12 }}
                                                tickLine={false}
                                                axisLine={false}
                                            />
                                            <YAxis
                                                tick={{ fill: 'var(--text-dim)', fontSize: 12 }}
                                                tickLine={false}
                                                axisLine={false}
                                                width={30}
                                            />
                                            <RechartsTooltip content={<CustomTooltip visitLabel={t('analytics_visits')} />} />
                                            <Area
                                                type="monotone"
                                                dataKey="visits"
                                                stroke="#5B6EF5"
                                                strokeWidth={2}
                                                fill="url(#areaGrad)"
                                            />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* Donut Chart */}
                            <div className="analytics-card">
                                <div className="analytics-card-header">
                                    <h3 className="analytics-card-title">{t('analytics_client_retention')}</h3>
                                </div>
                                <div className="analytics-chart-container analytics-donut-wrapper">
                                    <ResponsiveContainer width="100%" height={220}>
                                        <PieChart>
                                            <Pie
                                                data={retentionPercent}
                                                cx="50%"
                                                cy="45%"
                                                innerRadius={55}
                                                outerRadius={80}
                                                paddingAngle={4}
                                                dataKey="value"
                                                stroke="none"
                                            >
                                                {retentionPercent.map((entry, i) => (
                                                    <Cell key={i} fill={RETENTION_COLORS[i % RETENTION_COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Legend
                                                verticalAlign="bottom"
                                                formatter={(value, entry) => {
                                                    const item = retentionPercent.find(d => d.name === value);
                                                    const pct = item?.percent ?? item?.value ?? 0;
                                                    return (
                                                        <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                                                            {value} ({pct}%)
                                                        </span>
                                                    );
                                                }}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </section>

                        {/* ═══ 4. TOP CLIENTS TABLE ═══ */}
                        <section className="analytics-section">
                            <div className="analytics-card">
                                <div className="analytics-card-header">
                                    <h3 className="analytics-card-title">{t('analytics_top_clients')}</h3>
                                </div>
                                <div className="analytics-table-body">
                                    {topClients.length > 0 ? (
                                        topClients.map((client, i) => (
                                            <div key={i} className="analytics-client-row">
                                                <div className="analytics-client-avatar">
                                                    {client.name.charAt(0)}
                                                </div>
                                                <div className="analytics-client-info">
                                                    <span className="analytics-client-name">{client.name}</span>
                                                    <span className="analytics-client-stat">{client.visits} {t('analytics_visits')} · {client.stamps} {t('analytics_stamps')}</span>
                                                    <span className="analytics-client-stat" style={{ fontSize: 10, color: 'var(--text-dim)' }}>
                                                        {client.lastVisit ? `${t('last_visit')}: ${formatTimeAgo(client.lastVisit, t)}` : t('no_visits_yet')}
                                                    </span>
                                                </div>
                                                <div className="analytics-client-progress-wrapper">
                                                    <div className="analytics-client-progress-bar">
                                                        <div
                                                            className="analytics-client-progress-fill"
                                                            style={{ width: `${(client.stamps / client.threshold) * 100}%` }}
                                                        />
                                                    </div>
                                                    <span className="analytics-client-progress-label">
                                                        {client.stamps}/{client.threshold}
                                                    </span>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div style={{ padding: '24px 20px', textAlign: 'center', color: 'var(--text-dim)' }}>
                                            {t('analytics_no_clients')}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </section>

                        {/* ═══ 5. BOTTOM INSIGHT CARDS ═══ */}
                        <section className="analytics-section analytics-insights-grid">
                            <div className="analytics-insight-card">
                                <div className="analytics-insight-icon" style={{ background: 'rgba(250,204,21,0.12)', color: '#facc15' }}>
                                    <Trophy size={22} />
                                </div>
                                <div className="analytics-insight-body">
                                    <span className="analytics-insight-label">{t('analytics_best_day')}</span>
                                    <span className="analytics-insight-value">{insights.bestDay || '—'}</span>
                                    <span className="analytics-insight-sub">{insights.bestDayCount || 0} {t('analytics_total_visits')}</span>
                                </div>
                            </div>
                            <div className="analytics-insight-card">
                                <div className="analytics-insight-icon" style={{ background: 'rgba(99,102,241,0.12)', color: '#6366f1' }}>
                                    <Clock size={22} />
                                </div>
                                <div className="analytics-insight-body">
                                    <span className="analytics-insight-label">{t('analytics_peak_hour')}</span>
                                    <span className="analytics-insight-value">
                                        {insights.peakHourStart != null ? `${insights.peakHourStart}:00 – ${insights.peakHourEnd}:00` : '—'}
                                    </span>
                                    <span className="analytics-insight-sub">{insights.peakHourCount || 0} {t('analytics_total_visits')}</span>
                                </div>
                            </div>
                            <div className="analytics-insight-card">
                                <div className="analytics-insight-icon" style={{ background: 'rgba(16,185,129,0.12)', color: '#10b981' }}>
                                    <Gift size={22} />
                                </div>
                                <div className="analytics-insight-body">
                                    <span className="analytics-insight-label">{t('analytics_upcoming_rewards')}</span>
                                    <span className="analytics-insight-value">{insights.almostReward || 0} {t('analytics_clients')}</span>
                                    <span className="analytics-insight-sub">{t('analytics_one_stamp_away')}</span>
                                </div>
                            </div>
                        </section>
                    </>
                )}

            </div>

            {/* Bottom Nav — identical to Dashboard */}
            <nav className="bottom-nav-premium">
                <Link to="/dashboard" className="sidebar-brand-premium">
                    <Logo size={28} />
                    <span className="sidebar-brand-text">Wafiyo</span>
                </Link>
                <Link to="/dashboard" className="nav-tab-premium">
                    <LayoutDashboard size={24} />
                    <span>{t('dashboard')}</span>
                </Link>
                <Link to="/dashboard" state={{ tab: 'clients' }} className="nav-tab-premium">
                    <Users size={24} />
                    <span>{t('clients')}</span>
                </Link>
                <Link to="/dashboard/card-design" className="nav-tab-premium">
                    <Palette size={24} />
                    <span>{t('design')}</span>
                </Link>
                <Link to="/dashboard" state={{ tab: 'settings' }} className="nav-tab-premium">
                    <Settings size={24} />
                    <span>{t('settings')}</span>
                </Link>
            </nav>
        </div>
    );
}

// ─── MetricCard (identical to Dashboard) ─────────────────────
function MetricCard({ icon: Icon, trend, value, label }) {
    return (
        <div className="metric-card-compact">
            <div className="metric-header">
                <Icon size={16} />
                {trend !== 0 && trend !== undefined && (
                    <div className={`trend-indicator ${trend > 0 ? 'trend-up' : 'trend-down'}`}>
                        <TrendingUp size={12} style={{ transform: trend < 0 ? 'scaleY(-1)' : 'none' }} />
                        <span>{trend > 0 ? '+' : ''}{trend}%</span>
                    </div>
                )}
            </div>
            <div className="metric-value-box">
                <span className="metric-main-value">{value}</span>
                <span className="metric-label-premium">{label}</span>
            </div>
        </div>
    );
}
